import React, { useEffect, useRef, useState } from 'react';

interface SimpleRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onUploadImage: (file: File) => Promise<string>;
}

const toolbarButtonBaseClass =
  'rounded-lg border px-3 py-2 text-sm font-medium transition';

const editorSurfaceClass =
  'min-h-[420px] rounded-2xl border border-slate-200 bg-white px-5 py-4 text-base text-slate-900 shadow-sm focus:outline-none [&_p]:my-3 [&_h2]:my-5 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h3]:my-4 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:tracking-tight [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-sky-200 [&_blockquote]:pl-4 [&_blockquote]:text-slate-600 [&_a]:text-sky-700 [&_a]:underline [&_img]:my-5 [&_img]:rounded-2xl [&_img]:shadow-sm [&_iframe]:my-5 [&_iframe]:aspect-video [&_iframe]:w-full [&_iframe]:rounded-2xl';

type ToolbarState = {
  block: 'p' | 'h2' | 'h3' | 'blockquote' | null;
  bold: boolean;
  italic: boolean;
  link: boolean;
  unorderedList: boolean;
};

const isCommandSupported = (command: string) => {
  try {
    return document.queryCommandSupported(command);
  } catch {
    return false;
  }
};

const SimpleRichTextEditor: React.FC<SimpleRichTextEditorProps> = ({ value, onChange, onUploadImage }) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [toolbarState, setToolbarState] = useState<ToolbarState>({
    block: 'p',
    bold: false,
    italic: false,
    link: false,
    unorderedList: false,
  });

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.innerHTML !== value) {
      editor.innerHTML = value || '';
    }
  }, [value]);

  const syncFromEditor = () => {
    const editor = editorRef.current;
    if (!editor) return;
    onChange(editor.innerHTML);
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const updateToolbarState = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      setToolbarState((current) => ({ ...current, block: 'p', bold: false, italic: false, link: false, unorderedList: false }));
      return;
    }

    const anchorNode = selection.anchorNode;
    if (!anchorNode || !editor.contains(anchorNode)) {
      setToolbarState((current) => ({ ...current, block: 'p', bold: false, italic: false, link: false, unorderedList: false }));
      return;
    }

    const element = anchorNode.nodeType === Node.ELEMENT_NODE ? (anchorNode as HTMLElement) : anchorNode.parentElement;
    const blockTag = findClosestTag(element, editor, ['H2', 'H3', 'BLOCKQUOTE', 'P', 'LI', 'DIV']);
    const listTag = findClosestTag(element, editor, ['UL']);
    const linkTag = findClosestTag(element, editor, ['A']);

    setToolbarState({
      block:
        blockTag === 'H2'
          ? 'h2'
          : blockTag === 'H3'
            ? 'h3'
            : blockTag === 'BLOCKQUOTE'
              ? 'blockquote'
              : 'p',
      bold: queryState('bold'),
      italic: queryState('italic'),
      link: Boolean(linkTag),
      unorderedList: Boolean(listTag) || queryState('insertUnorderedList'),
    });
  };

  const runCommand = (command: string, commandValue?: string) => {
    focusEditor();
    if (!isCommandSupported(command)) return;
    document.execCommand(command, false, commandValue);
    syncFromEditor();
    updateToolbarState();
  };

  const applyHeading = (level: 'P' | 'H2' | 'H3' | 'BLOCKQUOTE') => {
    focusEditor();
    const normalizedLevel = level.toLowerCase() as 'p' | 'h2' | 'h3' | 'blockquote';
    const currentBlock = toolbarState.block ?? 'p';

    if (currentBlock === 'blockquote' && normalizedLevel !== 'blockquote') {
      document.execCommand('outdent', false);
    }

    if (normalizedLevel === 'blockquote') {
      if (currentBlock === 'blockquote') {
        document.execCommand('outdent', false);
        document.execCommand('formatBlock', false, 'p');
      } else {
        document.execCommand('formatBlock', false, 'blockquote');
      }
    } else {
      document.execCommand('formatBlock', false, normalizedLevel);
    }

    updateToolbarState();
    syncFromEditor();
  };

  const handleLink = () => {
    const url = window.prompt('Enter a URL');
    if (!url) return;
    runCommand('createLink', url.trim());
  };

  const handleEmbed = () => {
    const url = window.prompt('Paste a YouTube URL');
    if (!url) return;
    const embedUrl = toYouTubeEmbedUrl(url.trim());
    if (!embedUrl) {
      window.alert('That does not look like a valid YouTube URL.');
      return;
    }

    focusEditor();
    document.execCommand(
      'insertHTML',
      false,
      `<figure><iframe src="${embedUrl}" title="Embedded YouTube video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="lazy"></iframe></figure><p></p>`
    );
    syncFromEditor();
    updateToolbarState();
  };

  const handleImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const assetUrl = await onUploadImage(file);
      focusEditor();
      document.execCommand('insertImage', false, assetUrl);
      syncFromEditor();
      updateToolbarState();
    } finally {
      event.target.value = '';
    }
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const refresh = () => updateToolbarState();
    document.addEventListener('selectionchange', refresh);
    editor.addEventListener('keyup', refresh);
    editor.addEventListener('mouseup', refresh);
    editor.addEventListener('focus', refresh);

    refresh();

    return () => {
      document.removeEventListener('selectionchange', refresh);
      editor.removeEventListener('keyup', refresh);
      editor.removeEventListener('mouseup', refresh);
      editor.removeEventListener('focus', refresh);
    };
  }, []);

  const buttonClass = (active: boolean) =>
    active
      ? `${toolbarButtonBaseClass} border-sky-600 bg-sky-50 text-sky-700 shadow-sm`
      : `${toolbarButtonBaseClass} border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={buttonClass(toolbarState.block === 'p' && !toolbarState.unorderedList)} onClick={() => applyHeading('P')}>
          Paragraph
        </button>
        <button type="button" className={buttonClass(toolbarState.block === 'h2')} onClick={() => applyHeading('H2')}>
          H2
        </button>
        <button type="button" className={buttonClass(toolbarState.block === 'h3')} onClick={() => applyHeading('H3')}>
          H3
        </button>
        <button type="button" className={buttonClass(toolbarState.bold)} onClick={() => runCommand('bold')}>
          Bold
        </button>
        <button type="button" className={buttonClass(toolbarState.italic)} onClick={() => runCommand('italic')}>
          Italic
        </button>
        <button type="button" className={buttonClass(toolbarState.link)} onClick={handleLink}>
          Link
        </button>
        <button type="button" className={buttonClass(toolbarState.unorderedList)} onClick={() => runCommand('insertUnorderedList')}>
          Bullets
        </button>
        <button type="button" className={buttonClass(toolbarState.block === 'blockquote')} onClick={() => applyHeading('BLOCKQUOTE')}>
          Quote
        </button>
        <button
          type="button"
          className={buttonClass(false)}
          onClick={() => fileInputRef.current?.click()}
        >
          Image
        </button>
        <button type="button" className={buttonClass(false)} onClick={handleEmbed}>
          YouTube
        </button>
      </div>

      <div
        ref={editorRef}
        className={editorSurfaceClass}
        contentEditable
        suppressContentEditableWarning
        onInput={syncFromEditor}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
        className="hidden"
        onChange={handleImageSelection}
      />

      <p className="text-sm text-slate-500">
        Use the toolbar for headings, links, inline images, and YouTube embeds. Pasted content is allowed, but keep formatting simple.
      </p>
    </div>
  );
};

function queryState(command: string): boolean {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

function findClosestTag(
  start: HTMLElement | null | undefined,
  boundary: HTMLElement,
  tags: string[],
): string | null {
  let node: HTMLElement | null | undefined = start;
  while (node && node !== boundary) {
    if (tags.includes(node.tagName)) {
      return node.tagName;
    }
    node = node.parentElement;
  }
  return null;
}

function toYouTubeEmbedUrl(input: string): string | null {
  try {
    const url = new URL(input);
    if (url.hostname.includes('youtu.be')) {
      const id = url.pathname.replace('/', '').trim();
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (url.hostname.includes('youtube.com')) {
      const id = url.searchParams.get('v')?.trim();
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

export default SimpleRichTextEditor;
